import { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Pause, Play, Trash } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CampaignFormModel } from '../../../models/campaign';
import ImagePlaySection from '../ImagePlaySection';
import { ZonePlacement } from '../../../models/zoneplacement';
import { getZoneRatio } from '../../../utils';
import { cn } from '@/lib/utils';

interface AssetPreviewProps {
  assetUrl: string | null;
  assetType: string | null;
  assetDimension?: string;
  videoDuration?: number | undefined;
  handleDelete: () => void;
  onDurationChange: (newDuration: number) => Promise<void>;
  isCampaignEnded?: boolean;
  minDuration?: number;
  maxDuration?: number;
  campaignZonePlacementList?: ZonePlacement[] | undefined;
  isReadOnly?: boolean;
}

export default function AssetPreview({
  assetUrl,
  assetType,
  videoDuration,
  handleDelete,
  onDurationChange,
  isCampaignEnded,
  campaignZonePlacementList,
  isReadOnly,
}: AssetPreviewProps) {
  const { watch } = useFormContext<CampaignFormModel>();
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const asset = watch('file.asset');
  const duration = watch('file.duration');
  const selectedZones = watch('selectedZones');
  const selectedZonePlacements = watch('selectedZonePlacements');
  const currentCampaignId: number | undefined =
    Number(window.location.pathname.split('/')[2]) || undefined;

  const isDisabled = !asset && typeof currentCampaignId === 'number';

  const currentRatio = getZoneRatio(
    selectedZones || [],
    selectedZonePlacements || [],
    campaignZonePlacementList,
  );

  const renderDuration = (displayDuration: number | undefined) => (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{ height: '36px', alignItems: 'center' }}
    >
      <Typography variant="body2" sx={{ padding: '0 4px' }}>
        Duration:
      </Typography>
      <Typography variant="body2">
        {displayDuration !== undefined
          ? `${Math.round(displayDuration)} seconds`
          : ' N/A'}
      </Typography>
    </Box>
  );

  function toggleVideo() {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }

  if (selectedZones?.length === 0 && selectedZonePlacements?.length === 0) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: '400px',
        backgroundColor: 'secondary.contrastText',
        borderRadius: '8px',
        padding: '12px',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height="36px"
        sx={{ opacity: isCampaignEnded ? 0.5 : 1 }}
      >
        {isDisabled ? (
          renderDuration(duration)
        ) : videoDuration !== undefined ? (
          renderDuration(videoDuration)
        ) : (
          <ImagePlaySection onDurationChange={onDurationChange} />
        )}

        {!isReadOnly && (
          <Button
            aria-label="delete"
            disabled={isCampaignEnded}
            onClick={handleDelete}
            size="icon"
            variant="outline"
          >
            <Trash />
          </Button>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        sx={{
          width: '100%',
          flex: 1,
          opacity: isCampaignEnded ? 0.5 : 1,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: currentRatio === '16:9' ? '100%' : 'auto',
            position: 'relative',
            borderRadius: 'inherit',
          }}
        >
          {assetType === 'video/mp4' && assetUrl && (
            <Box
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              sx={{
                width: currentRatio === '16:9' ? '320px' : '180px',
                height: currentRatio === '16:9' ? '180px' : '320px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: 'inherit',
                  padding: '5px',
                }}
              >
                <source src={assetUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </Box>

              <Button
                aria-label={playing ? 'pause' : 'play'}
                data-testid="toggle-video"
                className={cn(
                  'absolute top-1/2 left-1/2 -translate-1/2',
                  playing && !hovered ? 'opacity-0' : 'opacity-100',
                )}
                onClick={toggleVideo}
                size="icon"
                type="button"
                variant="outline"
              >
                <Play className={cn(playing ? 'hidden' : 'inline-block')} />
                <Pause
                  className={cn(playing && hovered ? 'inline-block' : 'hidden')}
                />
              </Button>
            </Box>
          )}

          {assetType !== 'video/mp4' && assetUrl && (
            <Box
              sx={{
                width: currentRatio === '16:9' ? '320px' : '180px',
                height: currentRatio === '16:9' ? '180px' : '320px',
                backgroundColor: 'asset.main',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '5px',
              }}
            >
              <Box
                component="img"
                src={assetUrl || ''}
                alt="asset preview"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
